import { useEffect, useRef, useState } from "react";
import { Button } from "@livingmap/core-ui-v2";
import { Dialog } from "@livingmap/hippo";
import QRCode from "react-qr-code";

import styles from "./ShareDialog.module.scss";
import { useTranslation } from "react-i18next";

interface Props {
  dataQA: string;
  isOpen: boolean;
  url: string;
  description?: string;
  maxWidth?: number;
  overlayType?: "light" | "dark";
  onClose: () => void;
}

const ShareDialog: React.FC<Props> = ({
  dataQA,
  isOpen,
  url,
  description,
  maxWidth = 320,
  overlayType = "dark",
  onClose,
}) => {
  const { t } = useTranslation();

  const timeoutID = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isCopied, setIsCopied] = useState(false);
  const copyContainerRef = useRef<HTMLDivElement>(null);

  useEffect(
    () => () => {
      if (timeoutID.current) clearTimeout(timeoutID.current);
    },
    [],
  );

  const copyInsideDialog = () => {
    const container = copyContainerRef.current;
    if (!container?.isConnected) return false;

    // Native modal dialogs make everything outside them inert. Keep the
    // selection-based fallback inside this dialog rather than document.body.
    const input = document.createElement("textarea");
    input.value = url;
    input.readOnly = true;
    input.style.position = "fixed";
    input.style.opacity = "0";
    const previousFocus = document.activeElement;
    container.appendChild(input);
    try {
      input.focus();
      input.select();
      return document.execCommand("copy");
    } catch {
      return false;
    } finally {
      input.remove();
      if (previousFocus instanceof HTMLElement && previousFocus.isConnected) {
        previousFocus.focus({ preventScroll: true });
      }
    }
  };

  const handleOnCopy = async () => {
    if (timeoutID.current) {
      clearTimeout(timeoutID.current);
      timeoutID.current = null;
    }

    setIsCopied(false);
    let copied = false;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        copied = true;
      }
    } catch {
      // Fall back for browsers or embedding contexts that deny clipboard access.
    }
    if (!copied) copied = copyInsideDialog();
    if (!copied || !copyContainerRef.current?.isConnected) return;
    setIsCopied(true);

    timeoutID.current = setTimeout(() => {
      setIsCopied(false);
    }, 1500);
  };

  return (
    <Dialog
      dataQA={dataQA}
      isOpen={isOpen}
      onClose={onClose}
      width={maxWidth}
      closable
      title={t("share_dialog.title")}
      footer={
        <>
          <div ref={copyContainerRef} className={styles.urlContainer}>
            <p className={styles.url}>{url}</p>
          </div>
          <Button
            dataQA="copy-link-button"
            color="black"
            icon="ContentCopyIcon"
            onClick={handleOnCopy}
            className={styles.copyToClipboardButton}
            rounded
          >
            {isCopied
              ? t("share_dialog.button_text_confirmed")
              : t("share_dialog.button_text")}
          </Button>
        </>
      }
    >
      <div className={styles.dialogBody}>
        <div data-qa="qr-code" className={styles.qrCode}>
          <QRCode value={url} size={160} />
          {description && (
            <span className={styles.description}>{description}</span>
          )}
        </div>
      </div>
    </Dialog>
  );
};

export default ShareDialog;
